import { useState, useEffect } from 'react';
import { Form, Button, Spinner, Alert } from 'react-bootstrap';
import { FaEdit, FaSave, FaTimes, FaCalendarAlt, FaClock, FaMapMarkerAlt } from 'react-icons/fa';
import jornadaService from '../../services/jornadaService';
import sedeService from '../../services/sedeService';
import { toast } from 'react-toastify';

/**
 * Componente inline para editar jornada (fecha, hora, sede)
 * Estilo mobile-friendly similar al de TournamentManagement
 */
const EditarJornadaInline = ({ 
  show, 
  onHide, 
  jornada, 
  onJornadaEditada 
}) => {
  // Estados del formulario
  const [formData, setFormData] = useState({
    fecha: '',
    hora: '',
    sede_en_torneo_id: ''
  });
  
  // Estados de control
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sedesDisponibles, setSedesDisponibles] = useState([]);
  const [loadingSedes, setLoadingSedes] = useState(false);
  const [cambiosDetectados, setCambiosDetectados] = useState(false);

  // Cargar datos iniciales cuando se abre el modal
  useEffect(() => {
    if (show && jornada) {
      // Formatear fecha para input date (YYYY-MM-DD)
      const fechaFormateada = jornada.jornada_fecha ? 
        new Date(jornada.jornada_fecha).toISOString().split('T')[0] : '';
      
      setFormData({
        fecha: fechaFormateada,
        hora: jornada.jornada_hora || '',
        sede_en_torneo_id: jornada.sede_en_torneo_id || ''
      });
      
      setError(null);
      setCambiosDetectados(false);
      cargarSedesDisponibles();
    }
  }, [show, jornada]);

  // Cargar sedes disponibles para el torneo
  const cargarSedesDisponibles = async () => {
    if (!jornada?.torneo_id) return;
    
    setLoadingSedes(true);
    try {
      const response = await sedeService.getSedesPorTorneo(jornada.torneo_id);
      if (response.success) {
        setSedesDisponibles(response.data || []);
      }
    } catch (error) {
      console.error('Error cargando sedes:', error);
      setSedesDisponibles([]);
    } finally {
      setLoadingSedes(false);
    }
  };

  // DESHABILITADO: Detectar cambios en el formulario
  // useEffect(() => {
  //   if (!jornada) return;

  //   const fechaOriginal = jornada.jornada_fecha ?
  //     new Date(jornada.jornada_fecha).toISOString().split('T')[0] : '';

  //   const hayCambios =
  //     formData.fecha !== fechaOriginal ||
  //     formData.hora !== (jornada.jornada_hora || '') ||
  //     formData.sede_en_torneo_id !== (jornada.sede_en_torneo_id || '');

  //   console.log('🔍 DEBUG - Detectando cambios:');
  //   console.log('  - fechaOriginal:', fechaOriginal);
  //   console.log('  - formData.fecha:', formData.fecha);
  //   console.log('  - jornada.jornada_hora:', jornada.jornada_hora);
  //   console.log('  - formData.hora:', formData.hora);
  //   console.log('  - jornada.sede_en_torneo_id:', jornada.sede_en_torneo_id);
  //   console.log('  - formData.sede_en_torneo_id:', formData.sede_en_torneo_id);
  //   console.log('  - hayCambios:', hayCambios);

  //   setCambiosDetectados(hayCambios);
  // }, [formData, jornada]);

  // FORZAR BOTÓN SIEMPRE VISIBLE
  useEffect(() => {
    setCambiosDetectados(true);
  }, []);

  // Manejar cambios en inputs
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(null);
  };

  // Validar formulario
  const validarFormulario = () => {
    // TEMPORAL: Quitamos validación de cambios para debug
    // if (!cambiosDetectados) {
    //   setError('No se han detectado cambios para guardar');
    //   return false;
    // }

    if (formData.fecha) {
      const fechaSeleccionada = new Date(formData.fecha);
      const hoy = new Date();
      hoy.setHours(0, 0, 0, 0);
      
      if (fechaSeleccionada < hoy) {
        setError('La fecha no puede ser anterior a hoy');
        return false;
      }
    }

    if (formData.hora) {
      // Aceptar tanto HH:MM como HH:MM:SS
      const horaRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9](:[0-5][0-9])?$/;
      if (!horaRegex.test(formData.hora)) {
        setError('Formato de hora inválido. Use HH:MM o HH:MM:SS');
        return false;
      }
    }

    return true;
  };

  // Manejar envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault();

    console.log('🔍 DEBUG - handleSubmit iniciado');
    console.log('🔍 DEBUG - jornada:', jornada);
    console.log('🔍 DEBUG - formData:', formData);

    if (!validarFormulario()) {
      console.log('❌ DEBUG - Validación falló');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Preparar datos para enviar (solo campos modificados)
      const datosEdicion = {};

      const fechaOriginal = jornada.jornada_fecha ?
        new Date(jornada.jornada_fecha).toISOString().split('T')[0] : '';

      if (formData.fecha !== fechaOriginal) {
        datosEdicion.fecha = formData.fecha;
      }

      if (formData.hora !== (jornada.jornada_hora || '')) {
        datosEdicion.hora = formData.hora;
      }

      if (formData.sede_en_torneo_id !== (jornada.sede_en_torneo_id || '')) {
        datosEdicion.sede_en_torneo_id = parseInt(formData.sede_en_torneo_id);
      }

      console.log('🔍 DEBUG - datosEdicion preparados:', datosEdicion);
      console.log('🔍 DEBUG - Enviando petición a jornadaService.editarJornada...');

      // Enviar edición
      const response = await jornadaService.editarJornada(jornada.jornada_id, datosEdicion);

      console.log('✅ DEBUG - Respuesta recibida:', response);
      
      if (response.success) {
        toast.success(`✅ ${response.message}`, {
          position: "top-right",
          autoClose: 3000
        });
        
        // Notificar al componente padre
        if (onJornadaEditada) {
          onJornadaEditada();
        }
        
        onHide();
      } else {
        setError(response.message || 'Error al editar la jornada');
      }

    } catch (error) {
      console.error('Error editando jornada:', error);
      const errorMessage = error.response?.data?.message || 'Error interno del servidor';
      setError(errorMessage);
      toast.error(`❌ ${errorMessage}`, {
        position: "top-right",
        autoClose: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  // Resetear formulario al cerrar
  const handleClose = () => {
    setFormData({ fecha: '', hora: '', sede_en_torneo_id: '' });
    setError(null);
    setCambiosDetectados(false);
    onHide();
  };

  if (!show || !jornada) {
    return null;
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 1050,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: window.innerWidth <= 768 ? '0.5rem' : '1rem'
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          handleClose();
        }
      }}
    >
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: '8px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          width: '100%',
          maxWidth: window.innerWidth <= 768 ? '95%' : '500px',
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Header del Modal */}
        <div
          style={{
            padding: '1rem 1.5rem',
            borderBottom: '1px solid #dee2e6',
            backgroundColor: '#f8f9fa',
            position: 'relative'
          }}
        >
          <h2
            style={{
              fontSize: window.innerWidth <= 768 ? '18px' : '20px',
              fontWeight: '600',
              color: '#333',
              margin: 0,
              paddingRight: '2rem'
            }}>
            <FaEdit style={{ marginRight: '0.5rem', color: '#ffc107' }} />
            Editar Jornada {jornada.jornada_numero}
          </h2>
          <button
            onClick={handleClose}
            style={{
              position: 'absolute',
              right: '1rem',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#6c757d',
              padding: '0',
              width: '30px',
              height: '30px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ×
          </button>
        </div>

        <Form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
          {/* Body del Modal */}
          <div
            style={{
              padding: '1.5rem',
              backgroundColor: '#fff',
              flex: 1,
              overflowY: 'auto'
            }}
          >
            {error && (
              <Alert variant="danger" className="mb-3">
                <strong>Error:</strong> {error}
              </Alert>
            )}

            {/* Información actual */}
            <div className="mb-4 p-3 bg-light rounded">
              <h6 className="mb-2 text-primary">📋 Información Actual</h6>
              <div className="small text-muted">
                <div><strong>📅 Fecha:</strong> {jornada.jornada_fecha || 'No especificada'}</div>
                <div><strong>🕐 Hora:</strong> {jornada.jornada_hora || 'No especificada'}</div>
                <div><strong>📍 Sede:</strong> {jornada.sede_nombre || 'No especificada'}</div>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {/* Campo Fecha */}
              <Form.Group>
                <Form.Label style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <FaCalendarAlt style={{ marginRight: '0.5rem', color: '#007bff' }} />
                  Nueva Fecha
                </Form.Label>
                <Form.Control
                  type="date"
                  name="fecha"
                  value={formData.fecha}
                  onChange={handleInputChange}
                  min={new Date().toISOString().split('T')[0]}
                  style={{
                    fontSize: window.innerWidth <= 768 ? '16px' : '14px',
                    minHeight: window.innerWidth <= 768 ? '44px' : 'auto'
                  }}
                />
                <Form.Text className="text-muted">
                  Deje vacío para mantener la fecha actual
                </Form.Text>
              </Form.Group>

              {/* Campo Hora */}
              <Form.Group>
                <Form.Label style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <FaClock style={{ marginRight: '0.5rem', color: '#007bff' }} />
                  Nueva Hora
                </Form.Label>
                <Form.Control
                  type="time"
                  name="hora"
                  value={formData.hora}
                  onChange={handleInputChange}
                  placeholder="HH:MM"
                  style={{
                    fontSize: window.innerWidth <= 768 ? '16px' : '14px',
                    minHeight: window.innerWidth <= 768 ? '44px' : 'auto'
                  }}
                />
                <Form.Text className="text-muted">
                  Formato: HH:MM o HH:MM:SS. Deje vacío para mantener la hora actual
                </Form.Text>
              </Form.Group>

              {/* Campo Sede */}
              <Form.Group>
                <Form.Label style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <FaMapMarkerAlt style={{ marginRight: '0.5rem', color: '#007bff' }} />
                  Nueva Sede
                </Form.Label>
                {loadingSedes ? (
                  <div className="text-center py-2">
                    <Spinner animation="border" size="sm" />
                    <span className="ms-2">Cargando sedes...</span>
                  </div>
                ) : (
                  <Form.Select
                    name="sede_en_torneo_id"
                    value={formData.sede_en_torneo_id}
                    onChange={handleInputChange}
                    style={{
                      fontSize: window.innerWidth <= 768 ? '16px' : '14px',
                      minHeight: window.innerWidth <= 768 ? '44px' : 'auto'
                    }}
                  >
                    <option value="">Mantener sede actual</option>
                    {sedesDisponibles.map(sede => (
                      <option key={sede.sede_en_torneo_id} value={sede.sede_en_torneo_id}>
                        {sede.sede_nombre} - {sede.sede_poblacion}
                      </option>
                    ))}
                  </Form.Select>
                )}
                <Form.Text className="text-muted">
                  Seleccione una sede diferente o deje en blanco para mantener la actual
                </Form.Text>
              </Form.Group>

              {/* Aviso de notificaciones */}
              {cambiosDetectados && (
                <Alert variant="info" className="mb-0">
                  <strong>📱 Notificaciones:</strong> Los futbolistas inscritos recibirán una notificación automática sobre estos cambios.
                </Alert>
              )}
            </div>
          </div>

          {/* Footer del Modal */}
          <div
            style={{
              padding: '1rem 1.5rem',
              borderTop: '1px solid #dee2e6',
              backgroundColor: '#f8f9fa',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '0.5rem',
              minHeight: '80px',
              alignItems: 'center',

            }}
          >
            <Button
              variant="secondary"
              onClick={handleClose}
              disabled={loading}
              style={{
                minWidth: '80px',
                fontSize: '14px',
                padding: '0.5rem 1rem',
                minHeight: window.innerWidth <= 768 ? '44px' : 'auto'
              }}
            >
              <FaTimes className="me-2" />
              Cancelar
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={loading}
              style={{
                minWidth: '120px',
                fontSize: '14px',
                padding: '0.5rem 1rem',
                minHeight: window.innerWidth <= 768 ? '44px' : 'auto',
                display: 'block !important',
                visibility: 'visible !important',
                opacity: '1 !important',
                backgroundColor: '#007bff',
                borderColor: '#007bff',
                color: 'white'
              }}

            >
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Guardando...
                </>
              ) : (
                <>
                  <FaSave className="me-2" />
                  GUARDAR CAMBIOS
                </>
              )}
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default EditarJornadaInline;
